import {
  ApiRequest,
  closeAction,
  DataURLtoFile,
  SendError,
  SendSuccess,
  SendInfo,
} from '@/helpers';
import { utterances } from './utterances';
import { reactions } from './reactions';
import { localRecordings } from './localRecordings';
import { readSecretFile, audioUrlsFolderName } from '@/filesystem';
import { Sentry } from '@/vendors/Sentry';
import { KeepAwake } from '@capacitor-community/keep-awake';
import moment from 'moment';

export const recordings = {
  modules: {
    utterances,
    reactions,
    local: localRecordings,
  },
  namespaced: true,
  state: getInitialState(),
  actions: {
    async fetchRecordings(
      { commit, state, rootState, dispatch },
      { limit = 25, offset, uids, startAfter, minDuration, maxDuration, fromDate, toDate, teamId }
    ) {
      try {
        await rootState.organizationsPromise;
        const response = await ApiRequest({
          method: 'get',
          url: `${import.meta.env.VITE_API_URL}/recordings`,
          params: {
            limit,
            offset,
            organizationId: rootState.currentOrganizationId,
            uids,
            minDuration,
            maxDuration,
            fromDate,
            toDate,
            startAfter,
            teamId,
          },
        });
        for (const { id } of state.local.localRecordings) {
          if (response.data.recordings.find(({ localRecordingId }) => localRecordingId === id)) {
            await dispatch('local/deleteLocalRecording', id);
          }
        }
        commit('mergeRecordings', {
          recordings: response.data.recordings,
          organizationId: rootState.currentOrganizationId,
        });
        commit('recordingsLoaded');
        return response.data;
      } catch (err) {
        Sentry.captureException(err);
        SendError(err);
      }
    },
    async fetchRecording({ state, commit, dispatch }, { recordingId }) {
      try {
        const response = await ApiRequest({
          method: 'get',
          url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}`,
        });
        for (const { id } of state.local.localRecordings) {
          if (response.data.recording.localRecordingId === id) {
            await dispatch('local/deleteLocalRecording', id);
          }
        }
        const recording = response.data.recording;
        // TODO: merge tables
        commit('mergeRecordings', {
          recordings: [recording],
        });
        return recording;
      } catch (err) {
        Sentry.captureException(err);
        SendError(err);
      }
    },
    async fetchRecordingIfNeeded({ dispatch, getters }, { recordingId }) {
      if (!getters.getRecording(recordingId)) {
        return dispatch('fetchRecording', { recordingId });
      }
    },
    async updateRecording({ commit }, data) {
      const response = await ApiRequest({
        method: 'put',
        url: `${import.meta.env.VITE_API_URL}/recordings`,
        data,
      });
      // Update local recordings
      commit('updateRecording', response.data.recording);
      return response.data;
    },
    async deleteRecording({ commit }, data) {
      const response = await ApiRequest({
        method: 'delete',
        url: `${import.meta.env.VITE_API_URL}/recordings`,
        params: data,
      });
      await commit('deleteRecording', data.recordingId);
      // TODO: handle stats
      return response.data;
    },
    async queueUploadRecording({ state, dispatch }, { localRecordingId }) {
      if (
        ['uploading', 'queued'].includes(
          state.local.localRecordings.find(({ id }) => id === localRecordingId).status
        )
      ) {
        return;
      }
      await dispatch('local/editLocalRecording', {
        id: localRecordingId,
        status: 'queued',
      });
      const isCurrentlyUploading = state.local.localRecordings.some(
        ({ status }) => status === 'uploading'
      );
      if (isCurrentlyUploading) {
        SendSuccess(`Recording added to queue`);
      }
      dispatch('executeQueuedUpload');
    },
    async autoUploadLocalRecording({ dispatch, state, rootState }) {
      const currentOrganizationId = rootState.currentOrganizationId;

      if (!currentOrganizationId) return;

      const localRecordings = state.local.localRecordings.filter(
        (recording) => recording.organizationId === rootState.currentOrganizationId
      );

      if (
        state.isUploading ||
        localRecordings.some((recording) => ['queued', 'uploading'].includes(recording.status))
      )
        return;

      const localRecording = localRecordings.find((recording) => recording.status === 'local');

      if (!localRecording) return;

      await dispatch('local/editLocalRecording', {
        id: localRecording.id,
        status: 'queued',
      });
      dispatch('executeQueuedUpload');
    },
    async executeQueuedUpload({ dispatch, commit, state }) {
      if (state.isUploading) {
        console.log('Upload already in progress. Skipping...');
        return; // Prevent overlapping uploads
      }

      const localRecording = state.local.localRecordings.find(({ status }) => status === 'queued');
      if (!localRecording) {
        return;
      }

      commit('setIsUploading', true);
      try {
        await dispatch('uploadRecordingAudio', localRecording);
      } catch (err) {
        Sentry.captureException(err);
      } finally {
        commit('setIsUploading', false); // Clear the flag immediately after finishing the upload
      }

      await dispatch('executeQueuedUpload');
    },
    async uploadRecordingAudio(
      { commit, dispatch, rootState },
      {
        id,
        name,
        createdAt,
        aiTemplateId,
        audioUrlId,
        recordingId,
        uploadedChunks = 0,
        mimeType,
        integration,
        organizationId,
        chunkSize,
      }
    ) {
      // check what happens if not supported
      const { isSupported } = await KeepAwake.isSupported();
      if (isSupported) {
        await KeepAwake.keepAwake();
      }
      const notification = SendInfo('Uploading meeting. Please do not close app', {
        group: false,
        timeout: 0,
        spinner: true,
      });
      try {
        await dispatch('local/editLocalRecording', {
          id: id,
          status: 'uploading',
        });

        let audioUrl = await readSecretFile({
          fileName: audioUrlId,
          folder: audioUrlsFolderName,
        });

        if (!audioUrl) {
          await dispatch('local/deleteLocalRecording', id);
          notification({
            timeout: 1,
          });
          SendSuccess('Recording is uploaded and processed');
          return;
        }

        const blob = DataURLtoFile(audioUrl);
        audioUrl = null; // Free memory used by `audioUrl`

        let _recordingId = recordingId || null;
        const _chunkSize = chunkSize || (_recordingId ? 5 * 1024 * 1024 : 6 * 1024 * 1024);
        const totalChunks = Math.ceil(blob.size / _chunkSize);

        commit('local/setProgress', {
          id,
          value: Math.floor(uploadedChunks * 100) / totalChunks,
        });

        const _organizationId = organizationId || rootState.currentOrganizationId;

        if (!recordingId) {
          if (typeof createdAt.seconds !== 'number' || typeof createdAt.nanoseconds !== 'number') {
            throw new Error('Invalid createdAt time');
          }

          const { data } = await ApiRequest({
            method: 'post',
            url: `${import.meta.env.VITE_API_URL}/recordings/upload/start`,
            data: {
              name,
              createdAt,
              organizationId: _organizationId,
              localRecordingId: id,
              aiTemplateId,
              totalChunks,
              integration,
              mimeType,
            },
          });

          _recordingId = data.recordingId;
          await dispatch('local/editLocalRecording', {
            id,
            uploadedChunks: 0,
            chunkSize: _chunkSize,
            recordingId: _recordingId,
            totalChunks,
          });
        }

        for (let i = uploadedChunks; i < totalChunks; i++) {
          const start = i * _chunkSize;
          const end = Math.min(start + _chunkSize, blob.size);
          const chunk = blob.slice(start, end);

          const partNumber = i + 1;
          const formData = new FormData();
          formData.append('chunk', chunk);
          formData.append('recordingId', _recordingId);
          formData.append('organizationId', _organizationId);
          formData.append('partNumber', partNumber.toString());
          await ApiRequest({
            method: 'post',
            url: `${import.meta.env.VITE_API_URL}/recordings/upload/chunk`,
            headers: {
              'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: (progressEvent) => {
              const chunkProgress = Math.floor((i * 100) / totalChunks);
              const uploaded = Math.floor(
                chunkProgress +
                  Math.floor((progressEvent.loaded * 100) / (progressEvent.total * totalChunks))
              );
              commit('local/setProgress', {
                id,
                value: uploaded,
              });
            },
            data: formData,
          });
          await dispatch('local/editLocalRecording', {
            id,
            uploadedChunks: partNumber,
          });
        }
        try {
          const response = await ApiRequest({
            method: 'post',
            data: {
              recordingId: _recordingId,
              organizationId: _organizationId,
            },
            url: `${import.meta.env.VITE_API_URL}/recordings/upload/complete`,
          });
          notification({
            message: 'Upload complete!',
            spinner: false,
            type: 'positive',
            timeout: 2500,
            actions: [closeAction],
          });

          await dispatch('local/deleteLocalRecording', id);
          commit('mergeRecordings', {
            recordings: [{ ...response.data.recording, status: 'processing' }],
          });
          await dispatch('processRecording', { recordingId: response.data.recording.id });
          return response.data.recording;
        } catch (err) {
          if (err.status === 400) {
            await dispatch('local/resetLocalRecordingUpload', {
              id,
            });
            Sentry.captureException(err);
            notification({
              timeout: 1,
            });
            SendError('Uploading failed');
            return;
          }
          throw err;
        }
      } catch (err) {
        commit('local/setProgress', {
          progress: 0,
          id,
        });
        await dispatch('local/editLocalRecording', {
          id,
          status: 'local',
        });
        console.error(err);
        Sentry.captureException(err);
        notification({
          timeout: 1,
        });
        SendError('Uploading failed');
      } finally {
        const { isSupported } = await KeepAwake.isSupported();
        if (isSupported) {
          const { isKeptAwake } = await KeepAwake.isKeptAwake();
          if (isKeptAwake) {
            await KeepAwake.allowSleep();
          }
        }
      }
    },
    async processRecording({ commit }, { recordingId }) {
      const { data } = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/process`,
      });
      commit('mergeRecordings', { recordings: [data.recording] });
    },
    async editComment({ dispatch }, { threadId, commentId, editedComment, recordingId, elements }) {
      let response;
      if (threadId) {
        response = await ApiRequest({
          method: 'put',
          url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
          data: {
            replyId: commentId,
            threadId,
            comment: editedComment,
            elements,
          },
        });
      } else {
        response = await ApiRequest({
          method: 'put',
          url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
          data: {
            threadId: commentId,
            comment: editedComment,
            elements,
          },
        });
      }
      if (response.data) {
        dispatch('setThreads', {
          recordingId,
          threads: response.data.threads,
        });
      }
    },
    async addComment(
      { dispatch },
      { recordingId, timestamp, comment, elements, start, end, file }
    ) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('comment', comment);
      formData.append('elements', JSON.stringify(elements));
      formData.append('timestamp', timestamp);
      formData.append('start', start);
      formData.append('end', end);

      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
        data: formData,
      });
      dispatch('setThreads', {
        recordingId,
        threads: response.data.threads,
      });
    },
    async addReply({ dispatch }, { recordingId, threadId, comment, elements, file }) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('comment', comment);
      formData.append('elements', JSON.stringify(elements));
      formData.append('threadId', threadId);
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
        data: formData,
      });
      dispatch('setThreads', {
        recordingId,
        threads: response.data.threads,
      });
    },
    async deleteComment({ dispatch }, { recordingId, threadId, commentId }) {
      let response = null;
      if (threadId) {
        response = await ApiRequest({
          method: 'delete',
          url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
          data: {
            replyId: commentId,
            threadId,
          },
        });
      } else {
        response = await ApiRequest({
          method: 'delete',
          url: `${import.meta.env.VITE_API_URL}/recordings/${recordingId}/comments`,
          data: {
            threadId: commentId,
          },
        });
      }
      dispatch('setThreads', {
        recordingId,
        threads: response.data.threads,
      });
    },
    setThreads({ commit, getters }, { recordingId, threads }) {
      commit('updateRecording', {
        ...getters.getRecording(recordingId),
        threads,
      });
    },
    async getRecordingAnswer({}, { recordingId, question }) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/question`,
        data: {
          recordingId,
          question,
        },
      });
      return response.data;
    },
    async getRecordingReview({}, { recordingId, aiTemplateId }) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/review`,
        data: {
          recordingId,
          aiTemplateId,
        },
      });
      return response.data;
    },
    async shareRecording({ commit }, { recordingId }) {
      const response = await ApiRequest({
        method: 'post',
        url: `${import.meta.env.VITE_API_URL}/recordings/share`,
        data: {
          recordingId,
        },
      });
      const { shareUrl, shareExpiry } = response.data;
      commit('updateRecording', { shareUrl, shareExpiry });
      return response.data;
    },
  },
  mutations: {
    recordingsLoaded(state) {
      state.loaded = true;
    },
    mergeRecordings(state, { recordings }) {
      const recordingsMap = {};

      // Add new recordings to the map
      recordings.forEach((record) => {
        if (!recordingsMap[record.id]) {
          recordingsMap[record.id] = record;
        }
      });

      // Add state recordings to the map
      state.recordings.forEach((record) => {
        if (!recordingsMap[record.id]) {
          recordingsMap[record.id] = record;
        }
      });

      state.recordings = Object.values(recordingsMap).sort((a, b) => {
        return b.createdAt._seconds - a.createdAt._seconds;
      });
    },
    updateRecording(state, val) {
      const recordingIndex = state.recordings.findIndex((rec) => rec.id === val.id);
      const newRecording = { ...state.recordings[recordingIndex], ...val };
      if (recordingIndex !== -1) {
        // Replace the existing recording with the new one
        state.recordings.splice(recordingIndex, 1, newRecording);
      } else {
        // Add the new recording to the state if it doesn't exist
        state.recordings.push(newRecording);
      }
    },
    deleteRecording(state, val) {
      state.recordings = state.recordings.filter((recording) => {
        return recording.id !== val;
      });
    },
    resetState(state) {
      Object.assign(state, getInitialState());
    },
    resetRecordings(state) {
      state.recordings = [];
    },
    setIsUploading(state, val) {
      state.isUploading = val;
    },
  },
  getters: {
    getRecording: (state) => (recordingId) => {
      return state.recordings.find(({ id }) => recordingId === id);
    },
    getRecordingsByUid:
      (state, _, rootState) =>
        ({ uids, minDuration, maxDuration, fromDate, toDate, limit = state.recordings.length }) => {
          const localRecordings = state.local.localRecordings.filter(
            (recording) =>
              recording.organizationId === rootState.currentOrganizationId ||
            !recording.organizationId
          );
          if (!state.loaded) return localRecordings;

          const recordings = state.recordings
            .filter((recording) => recording.organizationId === rootState.currentOrganizationId)
            .filter((recording) => (uids ? uids.includes(recording.uid) : true))
            .filter((recording) => (minDuration ? recording.duration >= minDuration : true))
            .filter((recording) => (maxDuration ? recording.duration <= maxDuration : true))
            .filter((recording) =>
              fromDate ? moment.unix(recording.createdAt._seconds).isAfter(new Date(fromDate)) : true
            )
            .filter((recording) =>
              toDate ? moment.unix(recording.createdAt._seconds).isBefore(new Date(toDate)) : true
            );
          return [...localRecordings, ...recordings].slice(0, limit + localRecordings?.length);
        },
    getRecordingsByAll: (state) => {
      if (!state.loaded) {
        return [];
      }
      const recordings = state.recordings;
      return recordings.filter(({ status }) => status === 'processed');
    },
    getCommentById: (state) => (recordingId, commentId) => {
      const recordingIndex = state.recordings.findIndex((rec) => rec.id === recordingId);
      let replyId = -1;
      const threadIndex = state.recordings[recordingIndex].threads.findIndex(({ id, replies }) => {
        if (id !== commentId) {
          replyId = replies.findIndex((reply) => reply.id === commentId);
          return replyId === commentId;
        } else {
          return true;
        }
      });
      if (replyId >= 0) {
        return state.recordings[recordingIndex].threads[threadIndex].replies[replyId];
      } else {
        return state.recordings[recordingIndex].threads[threadIndex];
      }
    },
  },
};

function getInitialState() {
  return {
    loaded: false,
    isUploading: false,
    localRecordings: [],
    recordings: [],
  };
}
